import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, shareReplay, switchMap, map, startWith } from 'rxjs/operators';
import { Observable, of, combineLatest, BehaviorSubject } from 'rxjs';
import { DashboardApiService } from 'src/app/shared/services/api';
import { TechChangeSettingApiService } from 'src/app/shared/services/api';
import {
  TechChangeAgentDashboardListDto,
  TechChangeCardSummaryDetail,
  TechChangeCombinedSummaryResponseDto,
  TechChangeMonthlyExportSummaryResponseDto,
  TechChangeMonthlySummaryResponseDto,
} from 'src/app/shared/dto';
import { IsLoading, LoadingWrapper } from 'src/app/shared/decorators';
import { ToastrService } from 'ngx-toastr';
import { ResultTabsetUrlsEnum } from 'src/app/shared/enums';
import {
  AW_SELECTED_SIGNAL_PARAM,
  AW_START_CONSTRUCTOR_PARAM,
} from 'src/app/results/agentic-workflows/constants/deeplink.constants';

const LOADER_RUN_AUTOMATION: string = 'run-tech-change-automation';
const LOADER_AGENTS: string = 'get-tech-change-agents';
const LOADER_COMBINED_SUMMARY: string = 'get-tech-change-combined-summary';
const LOADER_REPORT_SUMMARY: string = 'get-tech-change-report-summary';

type TechChangeSummary =
  | TechChangeCombinedSummaryResponseDto
  | TechChangeMonthlySummaryResponseDto
  | TechChangeMonthlyExportSummaryResponseDto;

@UntilDestroy()
@Component({
  selector: 'app-tech-change-card-container',
  templateUrl: './tech-change-card-container.component.html',
  styleUrls: ['../../common-reporting-dashboard.scss'],
  standalone: false,
})
export class TechChangeCardContainerComponent implements OnInit {
  @IsLoading(LOADER_RUN_AUTOMATION) isRunAutomation$: Observable<boolean>;
  @IsLoading(LOADER_AGENTS) isGetAgentsLoading$: Observable<boolean>;
  @IsLoading(LOADER_COMBINED_SUMMARY) isGetCombinedSummaryLoading$: Observable<boolean>;
  @IsLoading(LOADER_REPORT_SUMMARY) isGetReportSummaryLoading$: Observable<boolean>;

  isShowChart: boolean = true;
  selectedAgent$: Observable<TechChangeAgentDashboardListDto | null>;
  summary$: Observable<TechChangeSummary>;
  isLoading$: Observable<boolean>;
  getAgents$: Observable<TechChangeAgentDashboardListDto[]>;
  selectedAgentSummaryDetail: TechChangeCardSummaryDetail | null = null;

  readonly resultTabsetUrlsEnum: typeof ResultTabsetUrlsEnum = ResultTabsetUrlsEnum;
  readonly newTechnologyDeeplinkParams: Record<string, string | number> = {
    [AW_SELECTED_SIGNAL_PARAM]: 'NEW_TECHNOLOGY',
    [AW_START_CONSTRUCTOR_PARAM]: 1,
  };

  private selectedAgentSubject = new BehaviorSubject<TechChangeAgentDashboardListDto | null>(null);

  constructor(
    private readonly dashboardApiService: DashboardApiService,
    private readonly techChangeSettingApiService: TechChangeSettingApiService,
    private readonly toaster: ToastrService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.getAgents$ = this.getTechChangeAgents().pipe(catchError(() => of([])), shareReplay(1));
    this.selectedAgent$ = this.selectedAgentSubject.asObservable();

    this.summary$ = this.selectedAgentSubject.pipe(
      startWith(null),
      switchMap((agent: TechChangeAgentDashboardListDto | null) => {
        if (agent) {
          if (agent.email_only) {
            return this.getTechChangeReportSummary(agent.id);
          }
          return this.getTechChangeExportSummary(agent.id);
        }
        return this.getTechChangeCombinedSummary();
      }),
      shareReplay(1),
    );

    this.isLoading$ = combineLatest([
      this.isGetAgentsLoading$,
      this.isGetCombinedSummaryLoading$,
      this.isGetReportSummaryLoading$,
      this.isRunAutomation$,
    ]).pipe(
      map((loaders: boolean[]) => loaders.some((loader: boolean) => loader)),
    );
  }

  updateSelectedAgent(agent: TechChangeAgentDashboardListDto | null): void {
    this.selectedAgentSummaryDetail = null;
    this.selectedAgentSubject.next(agent);
  }

  onWeeklySummarySet(summaryDetail: TechChangeCardSummaryDetail): void {
    this.selectedAgentSummaryDetail = summaryDetail;
  }

  showReport(): void {
    const reportId: string | null = this.selectedAgentSummaryDetail?.report_id ?? null;
    if (!reportId) {
      return;
    }
    this.router.navigate(['results/search'], {
      queryParams: { techChangeReportId: reportId },
    });
  }

  runAgent(): void {
    const selectedAgent: TechChangeAgentDashboardListDto | null = this.selectedAgentSubject.value;
    const automationId: number | undefined = selectedAgent?.id;
    if (!automationId) {
      return;
    }
    this.runTechChangeAutomation([automationId])
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => this.toaster.success('Success! Your automation has run.'),
        error: () => this.toaster.error('Failed to run automation. Please try again.'),
      });
  }

  @LoadingWrapper(LOADER_AGENTS)
  private getTechChangeAgents(): Observable<TechChangeAgentDashboardListDto[]> {
    return this.dashboardApiService.getTechChangeAgents();
  }

  @LoadingWrapper(LOADER_COMBINED_SUMMARY)
  private getTechChangeCombinedSummary(): Observable<TechChangeCombinedSummaryResponseDto> {
    return this.dashboardApiService.getTechChangeMonthlyCombinedSummary();
  }

  @LoadingWrapper(LOADER_REPORT_SUMMARY)
  private getTechChangeExportSummary(automationId: number): Observable<TechChangeMonthlyExportSummaryResponseDto> {
    return this.dashboardApiService.getTechChangeMonthlyExportSummary(automationId);
  }

  @LoadingWrapper(LOADER_REPORT_SUMMARY)
  private getTechChangeReportSummary(automationId: number): Observable<TechChangeMonthlySummaryResponseDto> {
    return this.dashboardApiService.getTechChangeMonthlyReportSummary(automationId);
  }

  @LoadingWrapper(LOADER_RUN_AUTOMATION)
  private runTechChangeAutomation(automationIds: number[]): Observable<unknown> {
    return this.techChangeSettingApiService.runTechChangeAutomation({ automationIds });
  }
}
